import type { RallyTrend, SongRequest } from '../services/requests';
import type { RallyChoice } from '../services/crowdRally';
import { isVotedOut, sharePercent, votedOutSeenKey } from './voteOut';

export const TREND_FLIP_POINTS = 3;
export const ALMOST_THERE_PEOPLE = 3;

type RallyRequest = Pick<SongRequest, 'id' | 'eventId' | 'status' | 'votedOut' | 'rallyTrend' | 'practiceMode'>;
type RallyCounts = Pick<RallyTrend, 'downvoteCount' | 'participantCount'>;

export type RallyTone = 'neutral' | 'good' | 'bad';
// What this attendee wants for the song: help it back, keep it out (they downvoted it), or no side
export type RallyGoal = 'back' | 'out' | 'none';

export function rallyNoticeSeenKey(eventId: string, requestId: string): string {
  return votedOutSeenKey(eventId, requestId);
}

export function rallyFlipSeenKey(eventId: string, requestId: string, flipCount: number): string {
  return `votebeats_rallyflip_${eventId}_${requestId}_${flipCount}`;
}

export function rallyAlmostSeenKey(eventId: string, requestId: string, flipCount: number): string {
  return `votebeats_rallyalmost_${eventId}_${requestId}_${flipCount}`;
}

function isQueuedAndOut(request: RallyRequest): boolean {
  return request.status === 'queued' && isVotedOut(request) && request.practiceMode !== true;
}

export function rallyNoticeQueue(
  requests: RallyRequest[],
  choices: Map<string, RallyChoice>,
  seen: (requestId: string) => boolean,
): string[] {
  return requests
    .filter(request => isQueuedAndOut(request) && !choices.has(request.id) && !seen(request.id))
    .map(request => request.id);
}

export function rallyFlipPopupIds(
  requests: RallyRequest[],
  choices: Map<string, RallyChoice>,
  seen: (key: string) => boolean,
): string[] {
  return requests
    .filter(request => {
      const trend = request.rallyTrend;
      return isQueuedAndOut(request)
        && choices.get(request.id) === 'in'
        && trend != null
        && trend.direction !== null
        && !seen(rallyFlipSeenKey(request.eventId, request.id, trend.flipCount));
    })
    .map(request => request.id);
}

// Once per approach: the server re-arms almostThereSent only on a flip to slipping, which bumps flipCount
export function rallyAlmostPopupIds(
  requests: RallyRequest[],
  choices: Map<string, RallyChoice>,
  seen: (key: string) => boolean,
): string[] {
  return requests
    .filter(request => {
      const trend = request.rallyTrend;
      return isQueuedAndOut(request)
        && choices.get(request.id) === 'in'
        && trend?.almostThereSent === true
        && !seen(rallyAlmostSeenKey(request.eventId, request.id, trend.flipCount));
    })
    .map(request => request.id);
}

export function rallyGoalOf(choice: RallyChoice | undefined, downvoted: boolean): RallyGoal {
  if (choice === 'in') return 'back';
  if (choice === 'dontCare') return 'none';
  return downvoted ? 'out' : 'none';
}

export function rallyTrendView(
  trend: RallyTrend | null | undefined,
  goal: RallyGoal,
): { label: string; tone: RallyTone } {
  if (trend?.direction === 'closer') {
    return { label: 'Trending back in', tone: goal === 'back' ? 'good' : goal === 'out' ? 'bad' : 'neutral' };
  }
  if (trend?.direction === 'slipping') {
    return { label: 'Trending to stay out', tone: goal === 'back' ? 'bad' : goal === 'out' ? 'good' : 'neutral' };
  }
  return { label: 'Just voted out', tone: 'neutral' };
}

// The trend carries the counts the server used, so the share always matches the trend it explains
export function rallyShareOf(trend: RallyTrend | null | undefined, fallbackShare: number): number {
  if (!trend || trend.participantCount <= 0) return fallbackShare;
  return sharePercent(trend.downvoteCount, trend.participantCount);
}

export function rallyProgress(trend: RallyTrend | null | undefined, share: number, returnLine: number): number {
  if (!trend || trend.outShare <= returnLine) return 0;
  const progress = (trend.outShare - share) / (trend.outShare - returnLine);
  return Math.min(1, Math.max(0, progress));
}

// Mirrors functions/src/utils/crowdRally.ts
export function peopleToReturn(counts: RallyCounts, returnLine: number): number | null {
  if (returnLine <= 0 || counts.participantCount <= 0) return null;
  return Math.max(0, Math.floor((100 * counts.downvoteCount) / returnLine) - counts.participantCount + 1);
}

export function downvotersToReturn(counts: RallyCounts, returnLine: number): number | null {
  if (counts.participantCount <= 0) return null;
  return Math.max(0, counts.downvoteCount - Math.ceil((returnLine * counts.participantCount) / 100) + 1);
}

function people(count: number): string {
  return count === 1 ? '1 more person' : `${count} more people`;
}

function downvoters(count: number): string {
  return count === 1 ? '1 downvoter' : `${count} downvoters`;
}

// "2 more people joining in without downvoting it would bring it back, or 1 downvoter changing their mind."
export function whatItTakes(trend: RallyTrend | null | undefined, returnPercent: number): string | null {
  if (!trend) return null;
  const joining = peopleToReturn(trend, returnPercent);
  const changing = downvotersToReturn(trend, returnPercent);
  if (joining === null || changing === null || joining <= 0 || changing <= 0) return null;
  return `${people(joining)} joining in without downvoting it would bring it back, or ${downvoters(changing)} changing their mind.`;
}
